import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Bookmark,
  BookmarkCheck,
  Check,
  Copy,
  Leaf,
  Maximize2,
  Minimize2,
  Send,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { api, type SavedChatReply } from "../api";
import { useAuth } from "../auth";
import { cx } from "../ui";
import { ChatMarkdown } from "./ChatMarkdown";
import { subscribeAskElm } from "./elmChatBus";

type ChatAction = { label: string; to?: string; prompt?: string };
type ChatReply = { reply: string; actions: ChatAction[]; suggestions: string[] };

type Message =
  | { author: "elm"; reply: ChatReply }
  | { author: "user"; text: string };

export function ElmChatbot() {
  const { state } = useAuth();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [view, setView] = useState<"chat" | "saved">("chat");
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [greeted, setGreeted] = useState(false);
  // Saved-replies state. `saved` is null until the Saved view has
  // been opened once; savedIds maps message index → saved row id so
  // a bookmarked bubble shows its saved state for the session.
  const [saved, setSaved] = useState<SavedChatReply[] | null>(null);
  const [savedIds, setSavedIds] = useState<Record<number, string>>({});
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const navigate = useNavigate();
  const bodyRef = useRef<HTMLDivElement>(null);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (open && !greeted && state.status === "authenticated") {
      void (async () => {
        try {
          // Rehydrate from the session-scoped DB history first.
          // Logout cascades the rows away, so the next sign-in
          // starts fresh by design — exactly the privacy posture
          // we want for a chat that owners paste contractor names
          // and scope details into. If the history call fails or
          // is empty, fall back to the greeter so the panel never
          // opens blank.
          const hist = await api.get<{
            data: Array<{
              id: string;
              role: "user" | "assistant";
              content: string;
              actions: ChatAction[] | null;
              suggestions: string[] | null;
              createdAt: string;
            }>;
          }>("/api/chat/elm/history");
          if (hist.data.length > 0) {
            const restored: Message[] = hist.data.map((row) =>
              row.role === "user"
                ? { author: "user", text: row.content }
                : {
                    author: "elm",
                    reply: {
                      reply: row.content,
                      actions: row.actions ?? [],
                      suggestions: row.suggestions ?? [],
                    },
                  },
            );
            setMessages(restored);
            setGreeted(true);
            return;
          }
          const reply = await api.get<ChatReply>("/api/chat/elm/greet");
          setMessages([{ author: "elm", reply }]);
          setGreeted(true);
        } catch {
          /* silent */
        }
      })();
    }
  }, [open, greeted, state.status]);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, busy, view]);

  const ask = useCallback(async (text: string) => {
    if (!text.trim()) return;
    setView("chat");
    setMessages((m) => [...m, { author: "user", text }]);
    setBusy(true);
    try {
      // History is read server-side from the session-scoped
      // chat_messages table; the client just sends the new
      // message. The server records both the user turn and the
      // assistant reply so a tab refresh rehydrates from
      // /chat/elm/history without losing context.
      const reply = await api.post<ChatReply>("/api/chat/elm", {
        message: text,
      });
      setMessages((m) => [...m, { author: "elm", reply }]);
    } catch {
      setMessages((m) => [
        ...m,
        {
          author: "elm",
          reply: {
            reply:
              "Sorry, I had trouble reaching the portal. Try again in a moment.",
            actions: [],
            suggestions: [],
          },
        },
      ]);
    } finally {
      setBusy(false);
    }
  }, []);

  // Allow other components (e.g. the new-project wizard) to open the
  // chatbot with a starter prompt via askElm("…").
  useEffect(() => {
    return subscribeAskElm((prompt) => {
      setOpen(true);
      setGreeted(true);
      void ask(prompt);
    });
  }, [ask]);

  if (state.status !== "authenticated") return null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    const text = input;
    setInput("");
    await ask(text);
  };

  const goTo = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  const onAction = (a: ChatAction) => {
    if (a.to) {
      goTo(a.to);
    } else if (a.prompt) {
      void ask(a.prompt);
    }
  };

  const copyText = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard API unavailable (http, old browser) — legacy path.
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } finally {
        document.body.removeChild(ta);
      }
    }
    if (copiedTimer.current) clearTimeout(copiedTimer.current);
    setCopiedKey(key);
    copiedTimer.current = setTimeout(() => setCopiedKey(null), 1600);
  };

  const saveReply = async (reply: ChatReply, messageIndex: number) => {
    try {
      const res = await api.post<{ data: SavedChatReply }>(
        "/api/chat/elm/saved",
        { content: reply.reply, actions: reply.actions ?? [] },
      );
      setSavedIds((m) => ({ ...m, [messageIndex]: res.data.id }));
      // Keep the Saved view fresh if it's already been loaded.
      setSaved((s) => (s === null ? s : [res.data, ...s]));
    } catch {
      /* leave the bookmark unsaved; the user can retry */
    }
  };

  const openSavedView = async () => {
    setView("saved");
    try {
      const res = await api.get<{ data: SavedChatReply[] }>(
        "/api/chat/elm/saved",
      );
      setSaved(res.data);
    } catch {
      setSaved((s) => s ?? []);
    }
  };

  const deleteSaved = async (id: string) => {
    try {
      await api.del(`/api/chat/elm/saved/${id}`);
      setSaved((s) => (s === null ? s : s.filter((r) => r.id !== id)));
      setSavedIds((m) => {
        const next: Record<number, string> = {};
        for (const [k, v] of Object.entries(m)) {
          if (v !== id) next[Number(k)] = v;
        }
        return next;
      });
    } catch {
      /* keep the row; the user can retry */
    }
  };

  const lastSuggestions = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i]!;
      if (msg.author === "elm") return msg.reply.suggestions ?? [];
    }
    return [];
  })();

  if (!open) {
    return (
      <button
        aria-label="Open Elm chatbot"
        title="Ask Elm"
        onClick={() => setOpen(true)}
        className="fixed bottom-[22px] right-[22px] z-50 w-[46px] h-[46px] rounded-full bg-brand-800 border-2 border-brand-600 flex items-center justify-center text-white shadow-pop hover:scale-105 transition-transform motion-reduce:hover:scale-100"
      >
        <Leaf
          size={19}
          className="animate-heartbeat motion-reduce:animate-none"
        />
      </button>
    );
  }

  const toolbarBtn =
    "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] text-ink-400 hover:text-brand-700 hover:bg-brand-50 transition-colors";

  return (
    <div
      className={cx(
        "fixed z-50 flex flex-col bg-white text-ink-700 shadow-pop overflow-hidden animate-slideUp",
        expanded
          ? "inset-0 sm:inset-4 rounded-none sm:rounded-2xl"
          : "bottom-6 right-6 w-[380px] max-w-[calc(100vw-32px)] h-[560px] max-h-[calc(100vh-80px)] rounded-2xl",
      )}
      role="dialog"
      aria-label="Elm chatbot"
    >
      <div className="px-4 py-3 flex items-center gap-3 bg-brand-800 text-white">
        {view === "saved" ? (
          <button
            aria-label="Back to chat"
            onClick={() => setView("chat")}
            className="p-1.5 rounded-lg text-white/85 hover:bg-white/15"
          >
            <ArrowLeft size={18} />
          </button>
        ) : (
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-brand-600 border-2 border-brand-500 text-white">
            <Leaf size={18} />
          </span>
        )}
        <div className="leading-tight flex-1">
          <strong className="block text-sm">
            {view === "saved" ? "Saved replies" : "Elm"}
          </strong>
          <span className="text-[11px] text-white/65">
            {view === "saved"
              ? "bookmarked answers, kept across sign-ins"
              : "portal assistant"}
          </span>
        </div>
        {view === "chat" && (
          <button
            aria-label="Saved replies"
            title="Saved replies"
            onClick={() => void openSavedView()}
            className="p-1.5 rounded-lg text-white/85 hover:bg-white/15"
          >
            <Bookmark size={17} />
          </button>
        )}
        <button
          aria-label={expanded ? "Exit full screen" : "Expand to full screen"}
          title={expanded ? "Exit full screen" : "Expand to full screen"}
          onClick={() => setExpanded((v) => !v)}
          className="p-1.5 rounded-lg text-white/85 hover:bg-white/15"
        >
          {expanded ? <Minimize2 size={17} /> : <Maximize2 size={17} />}
        </button>
        <button
          aria-label="Close"
          onClick={() => {
            setOpen(false);
            setExpanded(false);
          }}
          className="p-1.5 rounded-lg text-white/85 hover:bg-white/15"
        >
          <X size={18} />
        </button>
      </div>

      {view === "saved" ? (
        <div className="flex-1 overflow-y-auto p-4 bg-brand-50/40">
          {saved === null ? (
            <p className="text-sm text-ink-500">Loading…</p>
          ) : saved.length === 0 ? (
            <div className="text-sm text-ink-500 text-center mt-8 px-4">
              <Bookmark size={22} className="mx-auto mb-2 text-ink-300" />
              Nothing saved yet. Use the bookmark under any of Elm's answers
              to keep it here — saved answers stay after you sign out.
            </div>
          ) : (
            saved.map((r) => (
              <div
                key={r.id}
                className="mb-3 px-3 py-2.5 rounded-xl bg-white border border-ink-200"
              >
                <div className="text-[10.5px] text-ink-400 mb-1.5">
                  Saved{" "}
                  {new Date(r.savedAt).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </div>
                <div className="text-sm text-ink-700">
                  <ChatMarkdown text={r.content} onNavigate={goTo} />
                </div>
                {(r.actions ?? []).length > 0 && (
                  <div className="flex gap-1.5 mt-2 flex-wrap">
                    {(r.actions ?? []).map((a, j) => (
                      <button
                        key={j}
                        onClick={() => onAction(a)}
                        className="px-3 py-1 rounded-full bg-white border border-ink-200 text-xs hover:bg-brand-50 hover:border-brand-300 hover:text-brand-700"
                      >
                        {a.label}
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex gap-2 mt-2 pt-2 border-t border-ink-100">
                  <button
                    className={toolbarBtn}
                    onClick={() => void copyText(r.content, `saved-${r.id}`)}
                  >
                    {copiedKey === `saved-${r.id}` ? (
                      <>
                        <Check size={12} /> Copied
                      </>
                    ) : (
                      <>
                        <Copy size={12} /> Copy
                      </>
                    )}
                  </button>
                  <button
                    className={cx(toolbarBtn, "hover:text-status-rejected-fg")}
                    onClick={() => void deleteSaved(r.id)}
                  >
                    <Trash2 size={12} /> Remove
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <div ref={bodyRef} className="flex-1 overflow-y-auto p-4 bg-brand-50/40">
          {messages.map((m, i) => {
            if (m.author === "user") {
              return (
                <div key={i} className="flex justify-end mb-3">
                  <div className="max-w-[80%] px-3 py-2 rounded-2xl bg-brand-800 text-white text-sm whitespace-pre-wrap">
                    {m.text}
                  </div>
                </div>
              );
            }
            const savedId = savedIds[i];
            return (
              <div key={i} className="mb-3">
                <div className={cx(expanded ? "max-w-2xl" : "max-w-[85%]")}>
                  <div className="px-3 py-2 rounded-2xl bg-white border border-ink-200 text-ink-700 text-sm">
                    <ChatMarkdown text={m.reply.reply} onNavigate={goTo} />
                  </div>
                  <div className="flex items-center gap-1.5 mt-1 pl-1">
                    <button
                      className={toolbarBtn}
                      onClick={() => void copyText(m.reply.reply, `msg-${i}`)}
                      title="Copy this answer"
                    >
                      {copiedKey === `msg-${i}` ? (
                        <>
                          <Check size={12} /> Copied
                        </>
                      ) : (
                        <>
                          <Copy size={12} /> Copy
                        </>
                      )}
                    </button>
                    <button
                      className={cx(
                        toolbarBtn,
                        savedId && "text-brand-700",
                      )}
                      onClick={() =>
                        savedId ? void openSavedView() : void saveReply(m.reply, i)
                      }
                      title={
                        savedId
                          ? "Saved — open saved replies"
                          : "Save this answer for later"
                      }
                    >
                      {savedId ? (
                        <>
                          <BookmarkCheck size={12} /> Saved
                        </>
                      ) : (
                        <>
                          <Bookmark size={12} /> Save
                        </>
                      )}
                    </button>
                  </div>
                  {m.reply.actions.length > 0 && (
                    <div className="flex gap-1.5 mt-1.5 flex-wrap">
                      {m.reply.actions.map((a, j) => (
                        <button
                          key={j}
                          onClick={() => onAction(a)}
                          className="px-3 py-1 rounded-full bg-white border border-ink-200 text-xs hover:bg-brand-50 hover:border-brand-300 hover:text-brand-700"
                        >
                          {a.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {busy && (
            <div className="mb-3">
              <div className="inline-flex items-center gap-2 px-3 py-2 rounded-2xl bg-white border border-ink-200 text-ink-500 text-sm">
                <span className="spinner text-brand-700" />
                thinking…
              </div>
            </div>
          )}
        </div>
      )}

      {view === "chat" && lastSuggestions.length > 0 && !busy && (
        <div className={cx(
          "px-3 py-2.5 border-t border-ink-100 bg-ink-50",
          "flex gap-1.5 flex-wrap"
        )}>
          <Sparkles size={14} className="text-ink-400 self-center" />
          {lastSuggestions.map((s, i) => (
            <button
              key={i}
              onClick={() => void ask(s)}
              className="px-2.5 py-1 rounded-full bg-white border border-ink-200 text-xs text-ink-700 hover:bg-brand-50 hover:border-brand-300 hover:text-brand-700"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {view === "chat" && (
        <form
          onSubmit={submit}
          className="flex gap-2 p-3 border-t border-ink-100 bg-white"
        >
          <input
            className="input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about projects, fees, voting…"
            autoFocus
            disabled={busy}
          />
          <button
            type="submit"
            disabled={busy || !input.trim()}
            className="btn-primary"
            aria-label="Send"
          >
            <Send size={16} />
          </button>
        </form>
      )}
    </div>
  );
}
